import React, { useEffect, useState } from 'react';
import { css, keyframes } from '@emotion/react';
import { MdMic, MdMicOff } from 'react-icons/md';
import useIsClinician from 'vl-common/src/hooks/useIsClinician';
import { useAudioIndicator } from '@hooks/useAudioIndicator';
import { useRuntimeConfig } from '@vl-core/useRuntimeConfig';

const startUpAnimation = keyframes`
  0% {
    transform: scaleY(0);
  }
  50% {
    transform: scaleY(1);
  }
  100% {
    transform: scaleY(0);
  }
`;

const iconContainerStyle = css`
  position: relative;
  display: flex;
  align-items: end;
  justify-content: center;
`;

const micIndicatorSvgStyle = css`
  position: absolute;
  bottom: 9px;
  width: 100%;
  height: 8px;
`;

const micOnIndicatorStyle = css`
  fill: var(--productPrimary);
  opacity: 0.6;
  z-index: 1;
`;

const DynamicOnOffMicrophoneIcon = ({ isAudioEnabled }: { isAudioEnabled: boolean }) => {
  const { startAudioIndicator, level } = useAudioIndicator();
  const isClinician = useIsClinician();
  const [isStartupAnimationDone, setIsStartupAnimationDone] = useState(isClinician);

  useEffect(() => {
    startAudioIndicator();

    if (sessionStorage.getItem('audio-indicator-animation-done') || isStartupAnimationDone) return;
    sessionStorage.setItem('audio-indicator-animation-done', 'true');
    return () => {
      sessionStorage.removeItem('audio-indicator-animation-done');
    };
  }, []);

  const micOnIndicatorMovement = css`
    // Reduce fluctuation from very quiet noises
    transform: scaleY(${Math.min(level * 1.5, 1) < 0.2 ? 0 : Math.min(level * 1.5, 1)});
    transform-origin: bottom;
    will-change: transform;
    transform-box: fill-box;

    ${!isStartupAnimationDone &&
    css`
      animation-name: ${startUpAnimation};
      animation-duration: 5s;
    `};
  `;

  return isAudioEnabled ? (
    <div css={iconContainerStyle}>
      <MdMic />
      <svg css={micIndicatorSvgStyle} aria-hidden="true" viewBox="0 0 48 48" preserveAspectRatio="none">
        <rect
          onAnimationEnd={() => {
            setIsStartupAnimationDone(true);
          }}
          css={[micOnIndicatorStyle, micOnIndicatorMovement]}
          x="19.5px"
          y="0"
          width="9px"
          height="100%"
          rx="10"
          ry="10"
        />
      </svg>
    </div>
  ) : (
    <MdMicOff />
  );
};

const MicrophoneIcon = ({ isAudioEnabled }: { isAudioEnabled: boolean }) => {
  const { MIC_ACTIVITY_INDICATOR } = useRuntimeConfig();

  if (MIC_ACTIVITY_INDICATOR) {
    return <DynamicOnOffMicrophoneIcon isAudioEnabled={isAudioEnabled} />;
  }

  if (isAudioEnabled) return <MdMic />;

  return <MdMicOff />;
};

export default MicrophoneIcon;
